import { FunctionComponent } from 'react'

import {
  dehydrate,
  DehydratedState,
  QueryClient,
  useQueryClient,
} from '@tanstack/react-query'
import { GetServerSideProps } from 'next'

import { defaultConfig as queryConfig } from '@utils/react-query'
import {
  composeKropkaConfig,
  getIsBlickPlusOfferPage,
  getIsUserProfilePage,
  initializeQueryClientKeys,
  prefetchCUELiveData,
  prefetchJWPlayerKey,
  prefetchPage,
  prefetchPlaylist,
  prefetchSkeleton,
  prefetchSportData,
  prefetchSportEntities,
  processNonOverwritingKeyMap,
  requestContainsProblematicQueryParam,
  STATUS_404,
} from '@utils/server'
import { PageData } from '@hooks/usePageMetadata'
import JSONRenderer from '@components/JSONRenderer'

export interface AdSkeleton {
  version?: string
  environment?: 'staging' | 'prod'
}

export interface CatchAllPageServerProps {
  isError: boolean
  isBlickPlusOfferPage: boolean
  isUserProfilePage: boolean
  dehydratedQueryState: DehydratedState
  keysThatShouldNotOverwriteClientCacheIfExists: Record<string, true>
  documentProps?: {
    isSwissGeolocation: boolean
    disableThirdPartyScripts: boolean
    kropkaConfig: Record<string, unknown>
    preconnectToCUELiveDomain: boolean
    preconnectToSportDomain: boolean
    preconnectToSportDomainV2: boolean
  }
}

const CatchAllPage: FunctionComponent = () => {
  const queryClient = useQueryClient()

  const pageData = queryClient.getQueryData<PageData>(['page'])

  return <JSONRenderer>{pageData?.children ?? []}</JSONRenderer>
}

export default CatchAllPage

export const getServerSideProps: GetServerSideProps<
  CatchAllPageServerProps
> = async (context) => {
  if (requestContainsProblematicQueryParam(context)) {
    return STATUS_404
  }

  const queryClient = new QueryClient(queryConfig)

  const [skeletonFetchResult, pageFetchResult] = await Promise.allSettled([
    prefetchSkeleton(queryClient),
    prefetchPage({ queryClient, context }),
    prefetchJWPlayerKey({ queryClient, context }),
  ])

  if (skeletonFetchResult.status === 'rejected') {
    queryClient.clear()
    return STATUS_404
  }

  if (pageFetchResult.status === 'rejected') {
    queryClient.clear()
    return STATUS_404
  }

  const pageFetchResultValue = pageFetchResult.value

  if (pageFetchResultValue.customReturn) {
    queryClient.clear()
    return pageFetchResultValue.returnValue
  }

  await Promise.allSettled([
    prefetchSportEntities({
      queryClient,
      context,
    }),
    prefetchCUELiveData({ queryClient, context }),
    prefetchSportData({
      queryClient,
      context,
    }),
    prefetchPlaylist({
      queryClient,
      context,
    }),
  ])

  initializeQueryClientKeys({ queryClient, context })

  pageFetchResultValue.injectExtraHeaders(context)

  const finalProps: CatchAllPageServerProps = {
    isBlickPlusOfferPage: getIsBlickPlusOfferPage(context),
    isUserProfilePage: getIsUserProfilePage(context),
    isError: false,
    keysThatShouldNotOverwriteClientCacheIfExists: processNonOverwritingKeyMap([
      ['isSwissGeolocation'],
      ['viewportType'],
      ['clientViewportType'],
      ['viewportOrientation'],
      ['viewportDimensions'],
      ['navigationSkeleton'],
      ['footerSkeleton'],
      ['videoSkeleton'],
      ['blickPlusSkeleton'],
      ['featureFlagsSkeleton'],
      ['disableThirdPartyScripts'],
      ['body-scroll'],
      ['is-immersive-header-active'],
      ['theme', 'browser'],
      ['theme', 'user'],
      ['theme', 'app'],
      ['jwplayer-key'],
      ['sport-entities'],
      ['fmtCompetitionEntityIdsSortedSkeleton'],
    ]),
    dehydratedQueryState: dehydrate(queryClient),
    documentProps: {
      isSwissGeolocation: queryClient.getQueryData<boolean>([
        'isSwissGeolocation',
      ]) as boolean,
      disableThirdPartyScripts: !!queryClient.getQueryData<boolean>([
        'disableThirdPartyScripts',
      ]),
      preconnectToCUELiveDomain: !!queryClient.getQueryData<boolean>([
        'preconnect-domain-cue-live',
      ]),
      preconnectToSportDomain: !!queryClient.getQueryData<boolean>([
        'preconnect-domain-sport',
      ]),
      preconnectToSportDomainV2: !!queryClient.getQueryData<boolean>([
        'preconnect-domain-sport-v2',
      ]),
      kropkaConfig: composeKropkaConfig(queryClient),
    },
  }

  queryClient.clear()

  return {
    props: finalProps,
  }
}
